/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import { SwitchInput } from '../../ts-components/SwitchInput'
import { FormAction } from '../reducer/useFeedPostReducer'
import SelectMultiInput from '../../ts-components/SelectMultiInput'

interface PostSettingsProps {
  canReply: boolean
  activeCompetition: boolean
  dispatch: React.Dispatch<FormAction>
  isSelectedTypeOrg: boolean
  hasUpdateOrAdminPermission: boolean
  postToFeed: boolean
  visibleToRoles: string[]
  fieldErrors: string[]
  customError: { [key: string]: string }
}

// Role types configuration
export const ROLE_TYPES = [
  // { id: '0', name: 'Owner' },
  { id: '1', name: 'Admin' },
  { id: '2', name: 'League Manager' },
  { id: '3', name: 'Referee' },
  { id: '4', name: 'Scorekeeper' },
  { id: '5', name: 'Videographer' },
  { id: '6', name: 'Player' },
  { id: '7', name: 'Team Official' },
  { id: '8', name: 'Timekeeper' },
]

/**
 * PostSettings component - handles post configuration settings
 *
 * This component manages post-specific settings:
 * - Allow Comments: Whether users can reply to the post
 * - Target Active Competitions: Whether to limit notifications to active competitions
 *
 * The active competition toggle only shows when the selected entity is an organization
 * and the user has appropriate permissions.
 *
 * @param props - Component props for settings state and dispatch function
 * @returns JSX element representing the post settings section
 */
export const PostSettings: React.FC<PostSettingsProps> = ({
  canReply,
  activeCompetition,
  dispatch,
  isSelectedTypeOrg,
  hasUpdateOrAdminPermission,
  postToFeed,
  visibleToRoles,
  fieldErrors,
  customError,
}) => {
  console.log('🔄 PostSettings: Rendering component')

  return (
    <>
      {/* Target Audience selector */}
      {hasUpdateOrAdminPermission && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: 16px;
            display: none;
          `}
        >
          <SelectMultiInput
            label='Target Audience *'
            options={ROLE_TYPES.map((role) => ({
              label: role.name,
              value: role.id,
            }))}
            value={visibleToRoles}
            onChange={(value) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'visibleToRoles',
                payload: value,
              })
            }
            fieldErrors={fieldErrors}
            customError={customError}
            name='visibleToRoles'
          />
        </div>
      )}

      {/* Target Active Competitions - Only show for orgs with permissions */}
      {isSelectedTypeOrg && hasUpdateOrAdminPermission && (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            align-self: stretch;
            width: 100%;
            margin-bottom: 16px;
          `}
        >
          <SwitchInput
            title='Target Active Competitions'
            subtitle='Only send to users in currently active competitions'
            value={activeCompetition}
            onChange={(value) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'activeCompetition',
                payload: value,
              })
            }
          />
        </div>
      )}

      {/* Allow Comments - Only visible when posting to social feed */}
      {postToFeed && (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            align-self: stretch;
            width: 100%;
            margin-bottom: 16px;
          `}
        >
          <SwitchInput
            title='Allow Comments'
            subtitle='Let others join the conversation on this post'
            value={canReply}
            onChange={(value) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'canReply',
                payload: value,
              })
            }
          />
        </div>
      )}
    </>
  )
}
